import Link from "next/link";
import type { ComponentProps } from "react";
import Text from "@src/lib/ui/Text";

type HomeSectionProps = {
  children: React.ReactNode;
  title: string;
  /** When set, the section title is a link (e.g. to a full list view). */
  titleHref?: ComponentProps<typeof Link>["href"];
  /** Shown beside the title, e.g. a `HomeSectionCountBadge`. */
  titleMeta?: React.ReactNode;
};

export const HomeSectionTitle = ({
  title,
  titleHref,
}: Pick<HomeSectionProps, "title" | "titleHref">): React.ReactElement => {
  const titleNode = <Text variant="body-title">{title}</Text>;
  return titleHref ? (
    <Link
      href={titleHref}
      className="w-fit rounded-sm no-underline transition-colors outline-none hover:underline focus-visible:ring-2 focus-visible:ring-blue-4 focus-visible:ring-offset-2"
    >
      {titleNode}
    </Link>
  ) : (
    titleNode
  );
};

// The count pill beside a home section title (My Training, Check-ins).
export const HomeSectionCountBadge = ({
  count,
}: {
  count: number;
}): React.ReactElement => (
  // ds-allow: My Training's existing pill, shared as is so both sections match
  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-2 px-1.5 text-xs-v4 font-medium-v4 text-gray-9">
    {count}
  </span>
);

export const HomeSection = ({
  children,
  title,
  titleHref,
  titleMeta,
}: HomeSectionProps): React.ReactElement => {
  const titleNode = <HomeSectionTitle title={title} titleHref={titleHref} />;

  return (
    <div className="flex flex-col gap-4">
      {titleMeta != null ? (
        <div className="flex min-w-0 items-center gap-2">
          {titleNode}
          {titleMeta}
        </div>
      ) : (
        titleNode
      )}
      {children}
    </div>
  );
};
